import '../style.scss';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-phone-input-2/lib/style.css';
import { loadModules } from 'esri-loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faDownload } from '@fortawesome/free-solid-svg-icons';
import ConfigService from '../../../../shared/configService';
import QueryService from '../../../../shared/queryService';
import Utils from '../../../../shared/utils';
import LoadingOverlay from 'react-loading-overlay';
import { IFeature } from '@esri/arcgis-rest-types';
import { IEditReportState } from '../models/IEditReportState';
import { IActivitiesUploadTab } from '../models/IEditReportState';
import CookieService from '../../../../shared/cookieService';
import { IDashboard, IUploadShapeFields, IActivitiesFields } from '../../../../models/IConfig';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { saveAs } from "file-saver";

const ActivitiesUpload = (props: {state: IEditReportState, setState: Function}) => {
  const {t} = useTranslation('reports');
  const mapRef = React.createRef<HTMLDivElement>();
  const dashboardConfig: IDashboard  = ConfigService.getConfig().dashboard;
  const reporting_entity_type = window.localStorage.IUCN_USER_GROUP === 'PrivateSector' ? 2 : 1;
  
  // Renderer
  const pointsRenderer = {
    type: 'simple',
    symbol: {
      type: 'simple-marker',
      size: 10,
      color: dashboardConfig.map.countriesLayer.symbol.fillColor,
      outline: {  
        width: dashboardConfig.map.countriesLayer.symbol.outline.width,
        color: dashboardConfig.map.countriesLayer.symbol.outline.color
      }
    }
  };

  const polygonsRenderer = {
    type: "simple",
    symbol: {
      type: "simple-fill", 
      color: dashboardConfig.map.countriesLayer.symbol.fillColor,
      outline: {  
        width: dashboardConfig.map.countriesLayer.symbol.outline.width,
        color: dashboardConfig.map.countriesLayer.symbol.outline.color
      }
    }
  };

  let currentStoredActivities: any = null;

  // Delete dialog
  const [showDeleteDataDialog, setDeleteDataDialog] = useState(false);

  /**
   * Handle RevertToGlobal dialog
   */
   const handleDeleteDataDialogClose = () => setDeleteDataDialog(false);
   const handleDeleteDataDialogShow = () => setDeleteDataDialog(true);
   const handleDeleteDataDialogConfirm = () => {
    removeData();
    handleDeleteDataDialogClose();
   }
 

  // State
  interface ILocalState {
    isDataLoading: boolean;
    loadingMessage: string;
  }

  const [localState, setTheLocalState] = useState<ILocalState>({
    isDataLoading: false,
    loadingMessage: ''
  })

  /**
   * Set data loading on / off
   * @param loading
   * @param message
   */
   const setDataLoading = (loading: boolean, message: string) => {
    const obj: any = { ...localState };

    if(obj) {
      obj.isDataLoading = loading;
      obj.loadingMessage = message;
      setTheLocalState(obj);
    }
  }

  /**
   * Load the map
   */
  const loadMap = async () => {
    if(!(window as any).mapView) {
      loadModules(
        ['esri/Map', 'esri/views/MapView', 'esri/widgets/Home', 'esri/config'],
        { css: true }
      ).then(([Map, MapView, Home, esriConfig]) => {

        esriConfig.request.interceptors.push({
          urls: ConfigService.getConfig().agol?.operationalLayers.baseURL,
          before: (params: any) => {
              params.requestOptions.query = params.requestOptions.query || {};
              params.requestOptions.query.token = CookieService.getInstance().getSessionEsriToken();
          }
      });
        
        const map = new Map({
          basemap: 'topo',
        });

        const mapView = new MapView({
          container: 'mapContainer',
          map: map,
          center: [-2.405969, 14.0043],
          zoom: 3
        });
        
        const homeBtn = new Home({
          view: mapView
        });
        
        mapView.ui.add(homeBtn, 'top-left');

        mapView.when(() => {
          (window as any).mapView = mapView;
          (window as any).mapContainer = document.getElementById('mapContainer');

          if (currentStoredActivities && currentStoredActivities.layer) {
            addGraphicsToMap(currentStoredActivities.layer, currentStoredActivities.layer.features);
          }

          // Get activities
          if ((window as any).mapView) {
            getStoredActivities();
          }
        }, (error: any) => {
          console.error(error);
        });
      });
    } else {
     // (window as any).mapView.container = 'mapContainer';
     const parentContainer = document.getElementById('mapParentContainer')
     if(parentContainer) {
      parentContainer.innerHTML = '';
      parentContainer.append((window as any).mapContainer);
     }
    }
  }
  
  /**
   * Load
   */
  useEffect(() => {   
    loadMap();
    
    // Get activities
    if ((window as any).mapView) {
      getStoredActivities();
    }
  }, []);

  /**
   * Set parent state
   * @param prop
   * @param value 
   */
  /* const setParentState = (prop: string, value: any) => {

    if(props.state) {
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: IActivitiesUploadTab = { ...obj.activitiesUploadTab } as IActivitiesUploadTab;
      (subObj as any)[prop] = value;
      obj.activitiesUploadTab = subObj;
      props.setState(obj);
    }
  } */

  /**
   * Get stored activities
   */
  const getStoredActivities = async() => {
    const ptsFeatures: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.activitiesPoints.index), `${ConfigService.getConfig().agol.operationalLayers.activitiesFields.reportId} = '${props.state.report?.attributes.GlobalID}'`, false, '*');
    const polyFeatures: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.activitiesPolygons.index), `${ConfigService.getConfig().agol.operationalLayers.activitiesFields.reportId} = '${props.state.report?.attributes.GlobalID}'`, false, '*');
    
    if (ptsFeatures && ptsFeatures.length) {
      getActivitiesAggregations(ptsFeatures, 'point');
      addStoredActivitiesToMap(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.activitiesPoints.index), `${ConfigService.getConfig().agol.operationalLayers.activitiesFields.reportId} = '${props.state.report?.attributes.GlobalID}'`, 1);
    } else if (polyFeatures && polyFeatures.length) {
      getActivitiesAggregations(polyFeatures, 'polygon');
      addStoredActivitiesToMap(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.activitiesPolygons.index), `${ConfigService.getConfig().agol.operationalLayers.activitiesFields.reportId} = '${props.state.report?.attributes.GlobalID}'`, 2);
    }
  }

  /**
   * Add stored activities to map
   * @param url
   * @param where
   * @param typeCode
   */
  const addStoredActivitiesToMap = async(url: string, where: string, typeCode: number) => {
    loadModules(
      ['esri/layers/FeatureLayer'],
      { css: true }
    ).then(async ([FeatureLayer]) => {
      try {
        const fl = new FeatureLayer({url: `${url}?token=${CookieService.getInstance().getSessionEsriToken()}`, definitionExpression: where, renderer: typeCode === 1 ? pointsRenderer : polygonsRenderer});
        const query = {
          outFields: ['*'],
          returnGeometry: true,
          where: where
        }

        const res = await fl.queryFeatures(query);

        // Add data to the map
        if (res && res.features && res.features.length) {
          if ((window as any).mapView) {
            addGraphicsToMap(fl, res.features);
          } else {
            currentStoredActivities = {layer: fl, features: res.features}
          }
        }
      }
      catch(err: any) {
        console.error(err);
      }
    });
  }

  /**
   * Get activities aggregations
   * @param features
   * @param layerIndex
   * @param geometryType
   */
   const getActivitiesAggregations = async (features: IFeature[], geometryType: string) => {
    const fields: any = ConfigService.getConfig().agol.operationalLayers.activitiesFields;
    const obj: IEditReportState = { ...props.state } as IEditReportState;
    const subObj: IActivitiesUploadTab = { ...obj.activitiesUploadTab } as IActivitiesUploadTab;
    let shapefileUploadDate: string = '';

    // Store already loaded features
    subObj.alreadyUploadedShapefileFeatures = features;
    subObj.alreadyUploadedShapefileFeaturesType = geometryType;
    subObj.newUploadedShapefileFeatures = [];

    features.forEach( (feat: IFeature) => {
      const atts: any = feat.attributes;
      
      // Loading date
      if (!shapefileUploadDate && atts[fields.createdDate]) {
        const d: Date = new Date(atts[fields.createdDate]);

        if (d) {
          shapefileUploadDate = `${d.getFullYear()}.${('0'+(d.getMonth()+1)).slice(-2)}.${('0' + d.getDate()).slice(-2)} - ${('0' + d.getHours()).slice(-2)}:${('0' + d.getMinutes()).slice(-2)}`;
        }
      }

      if (atts) {
        // 1) Restored hectares
        if (atts[fields.haUnderRestoration]) {
          const val: number = Number(atts[fields.haUnderRestoration]);
          subObj.restoredHectares += val ? val : 0;
        }

        // 2) Investments (USD)
        if (atts[fields.investments]) {
          const val: number = Number(atts[fields.investments]);
          subObj.investments += val ? val : 0;
        }

        // 3) CO2 sequestred
        if (atts[fields.co2Sequestered]) {
          const val: number = Number(atts[fields.co2Sequestered]);
          subObj.cO2Sequestred += val ? val : 0;
        }

        // 4) Jobs created
        if (atts[fields.ecoJobsCreated]) {
          const val: number = Math.round(Number(atts[fields.ecoJobsCreated]));
          subObj.jobsCreated += val ? val : 0;
        }
      }          
    });
    subObj.shapefileUploadDate = shapefileUploadDate;
    subObj.reportHasShapeUploaded = true;
    obj.activitiesUploadTab = subObj;
    props.setState(obj);
  }

  /**
   * Handle shape file select
   */
  const onShapeFileSelect = (event: any) => {
    const fileName = event.target.value.toLowerCase();
    if (fileName.indexOf('.zip') !== -1) {      
      generateFeatureCollection(fileName);
    }
  }

  /**
   * Generate features from shapefile
   * @param fileName
   */
  const generateFeatureCollection = (fileName: string) => {
    loadModules(
      ['esri/request'],
      { css: true }
    ).then(([request]) => {
      try {
        const arr: string[] = fileName.split('.');
        const name = arr[0].replace('c:\\fakepath\\', '');

        const params: any = {
          'name': name,
          'targetSR': (window as any).mapView.spatialReference,
          'maxRecordCount': 1000,
          'enforceInputFileSizeLimit': true,
          'enforceOutputJsonSizeLimit': true
        };

        // generalize features to 10 meters for better performance
        params.generalize = true;
        params.maxAllowableOffset = 10;
        params.reducePrecision = true;
        params.numberOfDigitsAfterDecimal = 0;

        const myContent = {
          'filetype': 'shapefile',
          'publishParameters': JSON.stringify(params),
          'f': 'json',
        };

        // use the REST generate operation to generate a feature collection from the zipped shapefile
        setDataLoading(true, t('newReport.activitiesUpload.loadingData'));
        request(`${ConfigService.getConfig().agol.portalURL}/sharing/rest/content/features/generate`, {
          query: myContent,
          body: document.getElementById('uploadForm'),
          responseType: 'json'
        })
        .then((response: any) => {
          // const layerName = response.data.featureCollection.layers[0].layerDefinition.name;
          addShapefileToMap(response.data.featureCollection);
        })
        .catch((err: any) => {
          console.log(err);
          setDataLoading(false, '');
        });
      }
      catch(err: any) {
        console.error(err);
        setDataLoading(false, '');
        toast.error(t('newReport.activitiesUpload.loadShaoefileFail'));
      }
    });
  }

  /**
   * Add shapefile to the map
   * @param featureCollection
   */
  const addShapefileToMap = (featureCollection: any) => {
    loadModules(
      ['esri/Graphic', 'esri/layers/FeatureLayer', 'esri/layers/support/Field'],
      { css: true }
    ).then(async ([Graphic, FeatureLayer, Field]) => {
      try {
        let sourceGraphics: any[] = [];

        const layers = featureCollection.layers.map((layer: any) => {
          let geometryType = layer && layer.featureSet && layer.featureSet.features && layer.featureSet.features.length && layer.featureSet.features[0].geometry && layer.featureSet.features[0].geometry.x ? 'point' : 'polygon';

          let graphics: any[] = layer.featureSet.features.map((feature: any) => {
            return Graphic.fromJSON(feature);
          });
          sourceGraphics = sourceGraphics.concat(graphics);
          const featureLayer = new FeatureLayer({
            objectIdField: 'FID',
            source: graphics,
            renderer: geometryType === 'point' ? pointsRenderer : polygonsRenderer,
            fields: layer.layerDefinition.fields.map((field: any) => {
              return Field.fromJSON(field);
            })
          });
          return featureLayer;
        });
        
        // Add data to the map
        if (layers && layers.length) {
          addGraphicsToMap(layers[0], sourceGraphics);
        }

        // Handle the result
        handleResult(sourceGraphics);
      }
      catch(err: any) {
        console.error(err);
        setDataLoading(false, '');
      }
    });
  }

  /**
   * Add graphics to the map
   * @param featureCollection
   */
   const addGraphicsToMap = (layer: any, graphics: any[] = []) => {
    if((window as any).mapView) {
      // Remove existing data
      removeLayers();

      (window as any).mapView.map.add(layer);

      // Zoom to result
      if(graphics) {
        (window as any).mapView.goTo(graphics.filter((feature: any) => feature.geometry))
        .catch((error: any) => {          
          console.error(error);
          setDataLoading(false, '');
        });
      }
    } else {
      console.error(new Error('Mapview is undefined'))
    }
   }

  /**
   * Handle shapefile result
   * @param graphics
   */
  const handleResult = async (graphics: any[]) => {
    loadModules(
      ['esri/layers/FeatureLayer'],
      { css: true }
    ).then( async ([FeatureLayer]) => {
      try {
        const featureLayer = new FeatureLayer({
          url: `${ConfigService.getConfig().agol.gadmLayers.baseURL}/${ConfigService.getConfig().agol.gadmLayers.gadm_2.index}`
        });

        const gadm2Fields: any = ConfigService.getConfig().agol.gadmLayers.gadm_2.fields;
        const graphicsToUpload: any[] = [];
        const obj: IEditReportState = { ...props.state } as IEditReportState;
        const subObj: IActivitiesUploadTab = { ...obj.activitiesUploadTab } as IActivitiesUploadTab;
    
        // 1) Activities found in shapefile
        subObj.activitiesFound = graphics.length;

        // 2) Activities properly associated to a District
        let activitiesDistricts = 0;
        
        const promises: any[] = [];

        graphics.forEach( async(graphic: any) => {
          if (graphic && graphic.geometry && ["point", "polygon"].indexOf(graphic.geometry.type) !== -1) {
            const query = {
              geometry: graphic.geometry.type === "point" ? graphic.geometry : graphic.geometry.extent.center,
              distance: 1,
              spatialRelationship: 'intersects',
              outFields: ['*'],
              returnGeometry: false,
              where: '1=1'
            }
            
            promises.push(featureLayer.queryFeatures(query));
          }
        });      
            
        const all = await Promise.all(promises);
        
        all.forEach( (res: any, i: number) => {
          if (res.features.length) {
            const atts: any = res.features[0].attributes;
            graphics[i].attributes[gadm2Fields.gid0] = atts[gadm2Fields.gid0];
            graphics[i].attributes[gadm2Fields.gid1] = atts[gadm2Fields.gid1];
            graphics[i].attributes[gadm2Fields.gid2] = atts[gadm2Fields.gid2];
            graphicsToUpload.push(graphics[i]);
            activitiesDistricts += 1;
          }   
        });

        subObj.activitiesAssociatedToDistrict = activitiesDistricts;

        // Graphics to upload
        subObj.newUploadedShapefileFeatures = graphicsToUpload;
        obj.activitiesUploadTab = subObj;
        props.setState(obj);

        // Hide loading
        setDataLoading(false, '');
      }
      catch(err: any) {
        console.error(err);
        setDataLoading(false, '');
      }      
    });
  }
  
  /**
   * Download a sample shapefile
   */
  const downloadSampleShape = () => {
    saveAs(
      ConfigService.getConfig().agol.downloadAcitivitiesSampleUrl,
      "ActivitiesSamples.zip"
    );
  }

  /**
   * Remove layers
   */
   const removeLayers = () => {
    if((window as any).mapView.map.layers && (window as any).mapView.map.layers.length) {
      (window as any).mapView.map.removeAll();
    }
  }

  /**
   * Remove data
   */
  const removeData = async() => {
    try {
      removeLayers();
      
      const obj: IEditReportState = { ...props.state } as IEditReportState;
      const subObj: IActivitiesUploadTab = { ...obj.activitiesUploadTab } as IActivitiesUploadTab;
      subObj.shapefileUploadDate = '';
      subObj.reportHasShapeUploaded = false;
      obj.activitiesUploadTab = subObj;
      props.setState(obj);

      // Delete data
      const activitiesFields: any = ConfigService.getConfig().agol.operationalLayers.activitiesFields;
      const layerIndex: number = props.state.activitiesUploadTab?.alreadyUploadedShapefileFeaturesType === 'point' ? ConfigService.getConfig().agol.operationalLayers.activitiesPoints.index : ConfigService.getConfig().agol.operationalLayers.activitiesPolygons.index;
      let deletes: number[] = [];
      if (props.state.activitiesUploadTab?.alreadyUploadedShapefileFeatures && props.state.activitiesUploadTab?.alreadyUploadedShapefileFeatures.length) {
        deletes = props.state.activitiesUploadTab?.alreadyUploadedShapefileFeatures.map( (feat: IFeature) => {
          return feat.attributes[activitiesFields.objectId];
        });
      }
      
      setDataLoading(true, t('newReport.activitiesUpload.deletingData'));
      const res = await QueryService.applyEdits(layerIndex, [], [], deletes);
      getStoredActivities()
      setDataLoading(false, '');
      toast.success(t('newReport.activitiesUpload.deleteDataSuccess'));
    } catch(err: any) {
      console.error(err);
      setDataLoading(false, '');
      toast.error(t('newReport.activitiesUpload.deleteDataFail'));
    }
  }
  
  /**
   * Validate upload
   */
  const validateUpload = async() => {
    try {
      if (localState && props.state.activitiesUploadTab?.newUploadedShapefileFeatures.length) {
        const firstGeomType: any = props.state.activitiesUploadTab?.newUploadedShapefileFeatures[0].geometry.type;
        const layerIndex: number = firstGeomType === 'point' ? ConfigService.getConfig().agol.operationalLayers.activitiesPoints.index : ConfigService.getConfig().agol.operationalLayers.activitiesPolygons.index;
        const activitiesFields: IActivitiesFields = ConfigService.getConfig().agol.operationalLayers.activitiesFields;
        const shapeFields: IUploadShapeFields = ConfigService.getConfig().agol.uploadShapeFiles.fields;
        const gadm2Fields: any = ConfigService.getConfig().agol.gadmLayers.gadm_2.fields;

        // Adds
        const adds: any = props.state.activitiesUploadTab?.newUploadedShapefileFeatures.map( (feat: any) => {
          const obj: any = { attributes: {}, geometry: feat.geometry };
          const atts = feat.attributes;

          obj.attributes[activitiesFields.reportId] = props.state.report?.attributes.GlobalID;
          obj.attributes[activitiesFields.gid0] = gadm2Fields.gid0 in atts ? atts[gadm2Fields.gid0] : null;
          obj.attributes[activitiesFields.gid1] = gadm2Fields.gid1 in atts ? atts[gadm2Fields.gid1] : null;
          obj.attributes[activitiesFields.gid2] = gadm2Fields.gid2 in atts ? atts[gadm2Fields.gid2] : null;
          obj.attributes[activitiesFields.investments] = shapeFields.investment in atts ? atts[shapeFields.investment] : null;
          obj.attributes[activitiesFields.interventionType] = shapeFields.interventionType in atts ? atts[shapeFields.interventionType] : null;
          obj.attributes[activitiesFields.interventionType2] = shapeFields.interventionType2 in atts ? atts[shapeFields.interventionType2] : null;
          obj.attributes[activitiesFields.interventionType3] = shapeFields.interventionType3 in atts ? atts[shapeFields.interventionType3] : null;
          obj.attributes[activitiesFields.haUnderRestoration] = shapeFields.restoredHecatres in atts ? atts[shapeFields.restoredHecatres] : null;
          obj.attributes[activitiesFields.bioProtareaHa] = shapeFields.bioProtareaHa in atts ? atts[shapeFields.bioProtareaHa] : null;
          obj.attributes[activitiesFields.co2Sequestered] = shapeFields.cO2Sequestred in atts ? Number(atts[shapeFields.cO2Sequestred]) : null;
          obj.attributes[activitiesFields.ecoJobsCreated] = shapeFields.jobsCreated in atts ? atts[shapeFields.jobsCreated] : null;
          obj.attributes[activitiesFields.startYear] = shapeFields.startYear in atts ? atts[shapeFields.startYear] : null;
          obj.attributes[activitiesFields.endYear] = shapeFields.EndYear in atts ? atts[shapeFields.EndYear] : null;
          obj.attributes[activitiesFields.prjName] = shapeFields.prjName in atts ? atts[shapeFields.prjName] : null;
          obj.attributes[activitiesFields.reportingEntityType]=reporting_entity_type;

          return obj;
        });
        
        setDataLoading(true, t('newReport.activitiesUpload.uploadingData'));
        await QueryService.applyEdits(layerIndex, adds, [], []);
        getStoredActivities()
        setDataLoading(false, '');
        toast.success(t('newReport.activitiesUpload.uploadDataSuccess'));
      }
    }
    catch(err: any) {
      console.error(err);
      setDataLoading(false, '');
      toast.error(t('newReport.activitiesUpload.uploadDataFail'));
    }    
  }

  /**
   * Cancel upload
   */
   const cancelUpload = () => {

  }

  return(
    <div className='oneTabContent activitiesUploadTab'>
      <div className='itemsLeft'>        
        
        {props.state.activitiesUploadTab?.reportHasShapeUploaded &&
        <div className='resultDiv'>
          <div className='rowItem makeMargin'>
            <span className='boldSpan'>{t('newReport.activitiesUpload.shapefileUploadDate')} {props.state.activitiesUploadTab.shapefileUploadDate}</span>
          </div>

          <div className='rowItem'>
            <span>{t('newReport.activitiesUpload.restoredHectares')}: </span>
            <span className='numberSpan'>{props.state.activitiesUploadTab?.restoredHectares || '-'}</span>
          </div>

          <div className='rowItem'>
            <span>{t('newReport.activitiesUpload.investments')}: </span>
            <span className='numberSpan'>{props.state.activitiesUploadTab?.investments || '-'}</span>
          </div>

          <div className='rowItem'>
            <span>{t('newReport.activitiesUpload.cO2Sequestred')}: </span>
            <span className='numberSpan'>{props.state.activitiesUploadTab?.cO2Sequestred || '-'}</span>
          </div>

          <div className='rowItem'>
            <span>{t('newReport.activitiesUpload.jobsCreated')}: </span>
            <span className='numberSpan'>{props.state.activitiesUploadTab?.jobsCreated || '-'}</span>
          </div>

          <div className='rowItem buttonsDiv'>
            <button className='actionBtn primary' onClick={ handleDeleteDataDialogShow }>
              <span>{t('newReport.activitiesUpload.deleteData')}</span>
            </button>
          </div>
        </div>
        }

        {!props.state.activitiesUploadTab?.reportHasShapeUploaded &&
        <div>
          <div className='uploadItemDiv'>
            <form encType='multipart/form-data' method='post' id='uploadForm' onChange={ onShapeFileSelect }>
              <label className='fileLabel primary' htmlFor='inFile'>
                <span>{t('newReport.activitiesUpload.uploadNewShapeFile')}</span>
                <FontAwesomeIcon className='actionIcon' icon={ faUpload }/>
                <input type='file' name='file' id='inFile' hidden accept='.zip'/>
              </label>
            </form>

            <button className='actionBtn primary' onClick={ downloadSampleShape }>
                <span>{t('newReport.activitiesUpload.downloadSample')}</span>
                <FontAwesomeIcon className='actionIcon' icon={ faDownload }/>
            </button>
          </div>
          
          {props.state.activitiesUploadTab?.newUploadedShapefileFeatures && props.state.activitiesUploadTab?.newUploadedShapefileFeatures.length > 0 &&
          <div className='resultDiv'>
            <div className='rowItem'>
              <span className='numberSpan'>{props.state.activitiesUploadTab?.activitiesFound} </span>
              <span>{t('newReport.activitiesUpload.activitiesFound')} </span>
            </div>

            <div className='rowItem'>
            <span className='numberSpan'>{props.state.activitiesUploadTab?.activitiesAssociatedToDistrict} </span>
              <span>{t('newReport.activitiesUpload.activitiesAssociatedToDistrict')} </span>
            </div>
            {/* 
            <div className='rowItem'>
              <span className='numberSpan'>{props.state.activitiesUploadTab?.activitiesFallingInReportZone} </span>
              <span>{t('newReport.activitiesUpload.activitiesFallingInReportZone')} </span>
            </div>

            <div className='rowItem'>
              <span className='numberSpan'>{props.state.activitiesUploadTab?.activitiesFallingOutsideReportZone} </span>
              <span>{t('newReport.activitiesUpload.activitiesFallingOutsideReportZone')} </span>
            </div>
            */}

            <div className='rowItem buttonsDiv'>
              <button className='actionBtn primary' onClick={ validateUpload }>
                <span>{t('newReport.activitiesUpload.validateUpload')}</span>
              </button>
            </div>
          </div>
          }
        </div>
        }
      </div>
      <div className='itemsRight' id='mapParentContainer'>
        <div className='webmap' ref={mapRef} id='mapContainer'/>
      </div>

      <LoadingOverlay
      active={localState.isDataLoading}
      spinner
      text= {localState.loadingMessage}>
      </LoadingOverlay>

      <Modal show={showDeleteDataDialog} onHide={handleDeleteDataDialogClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('newReport.activitiesUpload.confirmDeleteDataTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('newReport.activitiesUpload.confirmRevertToGlobalContent')}</Modal.Body>
        <Modal.Footer>
          <button className="dialogBtn secondary" onClick={handleDeleteDataDialogClose}>
          {t('newReport.activitiesUpload.cancelDeleteData')}
          </button>
          <button className="dialogBtn primary" onClick={handleDeleteDataDialogConfirm}>
          {t('newReport.activitiesUpload.confirmDeleteData')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ActivitiesUpload;

